import React from 'react';
import { Box, Button, Checkbox, lighten, Tooltip, Typography, useTheme } from '@mui/material';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import {
    ContextualMenu,
    ContextualMenuItemProps,
    InformationTableRow,
} from '@europrocurement/flexy-components';
import { useSnackbar } from 'notistack';
import { useFormalitiesCtx } from '@europrocurement/l2d-modules';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';

type DossierFormaliteProcessListRowProps = {
    libelle: string;
    documentsCount: number;
    complete: boolean;
    optionsCount: number;
    externalUrl?: string;
    onExternalUrlClick: (externalUrl: string) => void;
    id: number;
};

type DossierFormaliteProcessListRowsProps = {
    formalities: DossierFormaliteProcessListRowProps[];
    selectedFormalitiesIds: number[];
    setSelectedFormalitiesIds: React.Dispatch<React.SetStateAction<number[]>>;
    isMulipleFormalities?: boolean;
    actions: Array<ContextualMenuItemProps>;
    process?: {
        validatorId: number;
        validatorLabel: string;
        statusLabel: string;
        statusFlag: number;
        onStatusClick?: () => void;
    };
};
const DossierFormaliteProcessListRows: React.FunctionComponent<DossierFormaliteProcessListRowsProps> =
    function (props) {
        const {
            formalities,
            selectedFormalitiesIds,
            setSelectedFormalitiesIds,
            isMulipleFormalities,
            actions,
            process,
        } = props;
        const { palette } = useTheme();
        const { enqueueSnackbar } = useSnackbar();
        const { getFormalityById } = useFormalitiesCtx();

        const handleCheckRow = (
            e: React.ChangeEvent<HTMLInputElement>,
            formalite: DossierFormaliteProcessListRowProps,
        ) => {
            if (e.target.checked) {
                if (formalities.length === selectedFormalitiesIds.length + 1) {
                    enqueueSnackbar("Vous ne pouvez pas sélectionner l'ensemble des formalités", {
                        variant: 'error',
                    });
                } else {
                    setSelectedFormalitiesIds((ids) => [...ids, formalite.id]);
                }
            } else {
                setSelectedFormalitiesIds(
                    selectedFormalitiesIds.filter((id) => id !== formalite.id),
                );
            }
        };

        const iconWrapperSx = {
            display: 'flex',
            alignItems: 'center',
            columnGap: '4px',
        };

        const documentCountLabelSx = {
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: 1.8,
            color: lighten(palette.secondary.main, 0.3),
        };

        const iconSx = {
            fontSize: '12px',
            lineHeight: 1,
            color: lighten(palette.secondary.main, 0.3),
        };

        const validatorSx = {
            whiteSpace: 'nowrap',
            fontWeight: 500,
            fontSize: '13px',
            lineHeight: 1.8,
            color: lighten(palette.secondary.main, 0.3),
        };

        return (
            <Box>
                {formalities.map((formalite, index) => {
                    const refFormalite = getFormalityById(formalite.id);
                    const formalityValidator = (refFormalite?.validators || []).find(
                        (v) => v.validator === process?.validatorId,
                    );

                    return (
                        <InformationTableRow
                            key={formalite.id}
                            cellsSx={[
                                {
                                    width: isMulipleFormalities ? '5%' : '0px',
                                    display: isMulipleFormalities ? 'table-cell' : 'block',
                                },
                                { width: '5%' },
                                { width: '40%' },
                                { width: isMulipleFormalities ? '25%' : '20%' },
                                { width: process ? '12.5%' : '30%' },
                                { width: process ? '17.5%' : '0%' },
                                { width: '5%' },
                            ]}
                            cels={[
                                <Checkbox
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={`'row'-${index}`}
                                    sx={{
                                        transition: 'opacity ease-in-out 300ms',
                                        pointerEvents: isMulipleFormalities ? 'auto' : 'none',
                                        opacity: isMulipleFormalities ? 1 : 0,
                                        width: isMulipleFormalities ? '100%' : '0px',
                                    }}
                                    checked={!!selectedFormalitiesIds.includes(formalite.id)}
                                    onChange={(e) => handleCheckRow(e, formalite)}
                                />,
                                index + 1,
                                formalite.libelle,
                                <Box
                                    sx={{ display: 'flex', alignItems: 'center', columnGap: '8px' }}
                                >
                                    {formalite.documentsCount > 0 && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'paperclip']}
                                                sx={iconSx}
                                            />
                                            <Typography
                                                variant="l2dcaption"
                                                sx={documentCountLabelSx}
                                            >
                                                {formalite.documentsCount}
                                            </Typography>
                                        </Box>
                                    )}
                                    {formalite.optionsCount > 0 && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'gear']}
                                                sx={iconSx}
                                            />
                                            <Typography
                                                variant="l2dcaption"
                                                sx={documentCountLabelSx}
                                            >
                                                {formalite.optionsCount}
                                            </Typography>
                                        </Box>
                                    )}
                                    {formalite.complete && (
                                        <Box sx={iconWrapperSx}>
                                            <FaOptionIcon
                                                icon={['fas', 'pen-field']}
                                                sx={iconSx}
                                            />
                                        </Box>
                                    )}
                                    {formalite.externalUrl && (
                                        <Box sx={iconWrapperSx}>
                                            <Tooltip title={formalite.externalUrl}>
                                                <Button
                                                    variant="text"
                                                    onClick={() =>
                                                        formalite?.onExternalUrlClick(
                                                            formalite.externalUrl as unknown as string,
                                                        )
                                                    }
                                                    sx={{
                                                        minWidth: 'auto',
                                                        width: 'auto',
                                                        padding: '0',
                                                    }}
                                                >
                                                    <FaOptionIcon
                                                        icon={['fas', 'link']}
                                                        sx={iconSx}
                                                    />
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    )}
                                    <Box sx={iconWrapperSx}>
                                        <Tooltip
                                            title={
                                                formalityValidator?.documents
                                                    ? 'Cette formalité est configurée pour communiquer les documents'
                                                    : 'Cette formalité est configurée pour ne pas communiquer les documents'
                                            }
                                        >
                                            <Box>
                                                <FaOptionIcon
                                                    icon={[
                                                        'fas',
                                                        formalityValidator?.documents
                                                            ? 'file-circle-check'
                                                            : 'file-circle-exclamation',
                                                    ]}
                                                    sx={iconSx}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                    <Box sx={iconWrapperSx}>
                                        <Tooltip
                                            title={
                                                formalityValidator?.forms
                                                    ? 'Cette formalité est configurée pour communiquer les informations des formulaires'
                                                    : 'Cette formalité est configurée pour ne pas communiquer les informations des formulaires'
                                            }
                                        >
                                            <Box>
                                                <FaOptionIcon
                                                    icon={[
                                                        'fas',
                                                        formalityValidator?.forms
                                                            ? 'memo-circle-check'
                                                            : 'memo-circle-info',
                                                    ]}
                                                    sx={iconSx}
                                                />
                                            </Box>
                                        </Tooltip>
                                    </Box>
                                </Box>,
                                <Typography
                                    variant="l2dcaption"
                                    sx={validatorSx}
                                >
                                    {process ? process.validatorLabel : 'Aucun'}
                                </Typography>,
                                <Box sx={iconWrapperSx}>
                                    {process ? (
                                        <DossierArticleProcessStatus
                                            statusFlag={process?.statusFlag}
                                            statusLabel={process?.statusLabel}
                                            onClick={process.onStatusClick}
                                        />
                                    ) : null}
                                </Box>,
                                <ContextualMenu
                                    items={actions}
                                    buttonSx={{ padding: 0 }}
                                />,
                            ]}
                        />
                    );
                })}
            </Box>
        );
    };

export default DossierFormaliteProcessListRows;
